/**
 * 반차 · 반반차 «시작 시각» 셈 (2026-09-16 · S6 · 서버 계약안 1판 `partialLeave`).
 *
 * ★★★ **고르는 것은 시작 시각 하나** — 끝 시각은 서버가 계산하고 화면은 같은 셈으로 미리 보여 줄 뿐이다
 *     (`endTime` 은 보내지 않는다). 그래서 길이 · 범위 · 눈금은 **서버가 준 `partialLeave` 값만** 쓴다.
 *     화면이 소정 · 휴게를 스스로 셈하면 서버와 1분만 어긋나도 신청이 400 이 된다.
 * ★★ 길이는 소정 비례(반차 = 소정 ÷ 2 · 반반차 = 소정 ÷ 4) — 그 값이 `halfMinutes` · `quarterMinutes` 로 온다.
 * ★ 휴게는 건너뛰지 않는다(사용자 확정) — 11:00 시작 반반차는 휴게가 들어 있어도 13:00 에 끝난다.
 * ★ 시각은 `HH:mm` 문자열로 주고받는다 — `Date` 로 바꾸면 «어느 날의» 시각인지가 따라붙는다(C4 TimePicker 와 같다).
 */

import { formatWorkMinutes } from './daily-work';

/** `GET /hr/attendance/policy?date=` 의 `partialLeave` — 이 키가 없으면 옛 서버다. */
export interface PartialLeavePolicy {
  available: boolean;
  /** `available=false` 일 때 — `OFF_DAY` · `LOAD_FAILED` · `CROSSES_MIDNIGHT`(서버가 늘릴 수 있다). */
  reason: string | null;
  earliestStart: string;
  latestEnd: string;
  stepMinutes: number;
  halfMinutes: number;
  quarterMinutes: number;
}

export type PartialLeaveKind = 'half' | 'quarter';

/** 오전/오후로 나뉘던 옛 코드 — 옛 기록 표시에만 쓰인다(새 목록에서는 숨긴다). */
export const LEGACY_PARTIAL_TYPES = ['half_am', 'half_pm', 'quarter_am', 'quarter_pm'] as const;

export function isPartialLeaveType(type: string | null | undefined): type is PartialLeaveKind {
  return type === 'half' || type === 'quarter';
}

export function isLegacyPartialType(type: string | null | undefined): boolean {
  return (LEGACY_PARTIAL_TYPES as readonly string[]).includes(type ?? '');
}

/** 옛 서버인가 — 근무제 응답에 `partialLeave` 키가 없다. */
export function isLegacyServer(policy: { partialLeave?: unknown } | null | undefined): boolean {
  return policy == null || !('partialLeave' in policy) || policy.partialLeave == null;
}

/**
 * `HH:mm` → 자정부터 분. 모양이 틀리면 `null`.
 * ⚠ `24:00` 은 받지 않는다 — `Date` 로 파싱하면 NaN 이 아니라 **다음 날 0시**가 되어 검사를 통과한다.
 */
export function parseHm(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function formatHm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 길이(분) — 서버가 준 값 그대로. */
export function partialLeaveMinutes(policy: PartialLeavePolicy, kind: PartialLeaveKind): number {
  return kind === 'half' ? policy.halfMinutes : policy.quarterMinutes;
}

/**
 * 「2시간」 · 「1시간 30분」 · 「45분」.
 *
 * ★ 표기는 **`lib/daily-work.ts` 의 `formatWorkMinutes` 한 곳**을 쓴다 (2026-09-26).
 *   견본 22개에서 **글자가 전부 같았다** — 그래서 옮겨도 보이는 글자가 안 바뀐다(회귀 0).
 *   ⚠ 0 이하·NaN 은 공용 함수가 「0분」으로 막는다(종전에는 음수가 「-1시간 -30분」이 됐다).
 */
export function durationText(minutes: number): string {
  return formatWorkMinutes(minutes);
}

/**
 * 고를 수 있는 시작 구간 — 서버 계약 §9: `[ceil(earliestStart), floor(latestEnd − 길이)]`(눈금 = `stepMinutes`).
 * ★ 최소는 **올림**, 최대는 **내림** — 09:05 출근이면 09:10 부터, 끝이 퇴근을 넘지 않게.
 * 구간이 비면(길이가 근무 시간보다 길다) `null` — 그날 그 유형은 쓸 수 없다(서버는 LEAVE_TIME_OUT_OF_HOURS).
 */
export function startWindow(policy: PartialLeavePolicy, kind: PartialLeaveKind): { min: string; max: string } | null {
  const earliest = parseHm(policy.earliestStart);
  const end = parseHm(policy.latestEnd);
  if (earliest == null || end == null) return null;
  const step = Math.max(1, policy.stepMinutes);
  const min = Math.ceil(earliest / step) * step;
  const max = Math.floor((end - partialLeaveMinutes(policy, kind)) / step) * step;
  return max < min ? null : { min: formatHm(min), max: formatHm(max) };
}

/** 가장 이른 시작(눈금 올림) — 구간이 비면 `null`. */
export function earliestStart(policy: PartialLeavePolicy, kind: PartialLeaveKind): string | null {
  return startWindow(policy, kind)?.min ?? null;
}

/** 가장 늦은 시작(눈금 내림) — 구간이 비면 `null`. */
export function latestStart(policy: PartialLeavePolicy, kind: PartialLeaveKind): string | null {
  return startWindow(policy, kind)?.max ?? null;
}

/** 끝 시각 미리 보기 — 시작이 틀리면 `null`. */
export function endTimeFor(policy: PartialLeavePolicy, kind: PartialLeaveKind, start: string | null | undefined): string | null {
  const s = parseHm(start);
  if (s == null) return null;
  const end = s + partialLeaveMinutes(policy, kind);
  return end >= 24 * 60 ? null : formatHm(end);
}

export type StartCheck = 'ok' | 'unavailable' | 'format' | 'step' | 'before' | 'after';

/** 시작 시각 판정 — 서버 400 과 같은 순서(모양 → 눈금 → 범위). 막힌 날이면 `unavailable`. */
export function checkStart(policy: PartialLeavePolicy, kind: PartialLeaveKind, start: string | null | undefined): StartCheck {
  if (!policy.available) return 'unavailable';
  const s = parseHm(start);
  if (s == null) return 'format';
  const step = Math.max(1, policy.stepMinutes);
  if (s % step !== 0) return 'step';
  const win = startWindow(policy, kind);
  // 구간이 비면 어떤 시각도 «범위 밖»이다(서버 LEAVE_TIME_OUT_OF_HOURS).
  if (!win) return 'after';
  if (s < (parseHm(win.min) ?? 0)) return 'before';
  if (s > (parseHm(win.max) ?? -1)) return 'after';
  return 'ok';
}

/** 저장된 구간 「14:00–16:00」 — 옛 기록 · 종일이면 `null`(지어내지 않는다). */
export function leaveTimeRangeText(leave: { startTime?: string | null; endTime?: string | null }): string | null {
  if (!leave.startTime || !leave.endTime) return null;
  return `${leave.startTime}–${leave.endTime}`;
}

/** 반차 · 반반차를 못 쓰는 날의 문구 — 모르는 이유는 뭉뚱그리되 막는다. */
export function unavailableText(reason: string | null | undefined): string {
  switch (reason) {
    case 'OFF_DAY': return '그날은 휴무일이라 반차 · 반반차를 신청할 수 없습니다.';
    case 'LOAD_FAILED': return '그날 근무제를 읽지 못해 반차 · 반반차를 신청할 수 없습니다. 잠시 뒤 다시 시도해 주세요.';
    case 'CROSSES_MIDNIGHT': return '자정을 넘는 교대 근무일에는 반차 · 반반차를 신청할 수 없습니다.';
    default: return '그날은 반차 · 반반차를 신청할 수 없습니다.';
  }
}

/** 서버 거절 본문(계약안 §2) — 필요한 칸만. */
export interface LeaveTimeErrorBody {
  code?: string;
  reason?: string | null;
  allowed?: { start?: string; end?: string };
  requested?: { startTime?: string; endTime?: string };
  conflict?: { typeLabel?: string | null; type?: string; startTime?: string | null; endTime?: string | null };
}

/**
 * 거절 코드 → 사람이 읽는 문구. 이 셈이 모르는 코드면 `null`(부르는 쪽이 종전 오류 문구를 쓴다).
 * ★ 범위 · 겹침은 «무엇과 · 어디까지»를 적는다 — 「잘못된 시각」만으로는 다음에 무엇을 고를지 모른다.
 */
export function leaveTimeErrorText(body: LeaveTimeErrorBody | null | undefined): string | null {
  if (!body?.code) return null;
  switch (body.code) {
    case 'LEAVE_TIME_OUT_OF_HOURS': {
      const a = body.allowed;
      const r = body.requested;
      const range = a?.start && a?.end ? `근무 시간 ${a.start}–${a.end} 안에서 고르세요` : '근무 시간 안에서 고르세요';
      return r?.startTime && r?.endTime ? `${range} — ${r.startTime}–${r.endTime} 은 벗어납니다.` : `${range}.`;
    }
    case 'LEAVE_PERIOD_OVERLAP': {
      const c = body.conflict;
      if (!c) return null;
      const what = c.typeLabel ?? '휴가';
      const when = c.startTime && c.endTime ? `${c.startTime}–${c.endTime}` : '종일';
      return `이미 낸 ${what}(${when})와 겹칩니다. 다른 시작 시각을 고르세요.`;
    }
    case 'WORK_POLICY_UNAVAILABLE':
      return unavailableText(body.reason);
    case 'INVALID_LEAVE_TIME':
    case 'LEAVE_TIME_REQUIRED':
    case 'LEAVE_TIME_NOT_ALLOWED':
      return '시작 시각을 다시 골라 주세요.';
    default:
      return null;
  }
}
